import React, { useEffect, useRef, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  BarElement,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { useQuery } from '@tanstack/react-query';
import { Calendar, ChevronDown } from 'lucide-react';
import { transactionService } from '@/services/transactionService';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

type PeriodType = 'week' | 'month' | 'year';

interface MonthlySalesChartProps {
  type?: 'line' | 'bar';
  height?: number;
}

const MonthlySalesChart: React.FC<MonthlySalesChartProps> = ({ 
  type = 'line', 
  height = 300 
}) => {
  const chartRef = useRef<ChartJS>(null);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodType>('month');
  const [showPeriodSelector, setShowPeriodSelector] = useState(false);

  // Query for sales data based on selected period
  const { data: salesData, isLoading, error } = useQuery({
    queryKey: ['salesData', selectedPeriod],
    queryFn: () => {
      console.log(`🔍 SalesChart: Calling getSalesData for period: ${selectedPeriod}`);
      switch (selectedPeriod) {
        case 'week':
          return transactionService.getWeeklySalesData();
        case 'month':
          return transactionService.getMonthlySalesData();
        case 'year':
          return transactionService.getYearlySalesData();
        default:
          return transactionService.getMonthlySalesData();
      }
    },
    staleTime: 300000, // 5 minutes
    retry: 1,
    refetchOnWindowFocus: false,
  });


  // Generate demo data if no real data available
  const generateDemoData = () => {
    const currentDate = new Date();
    let labels: string[] = [];
    let data: number[] = [];

    switch (selectedPeriod) {
      case 'week':
        // Generate 7 days for current week
        const startOfWeek = new Date(currentDate);
        startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1);

        for (let i = 0; i < 7; i++) {
          const date = new Date(startOfWeek);
          date.setDate(startOfWeek.getDate() + i);
          labels.push(date.toLocaleDateString('es-ES', {
            weekday: 'short',
            day: 'numeric'
          }));
          data.push(Math.round(10000 + Math.random() * 20000));
        }
        break;

      case 'month':
        // Generate weeks for current month
        const firstDay = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const lastDay = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        const numberOfWeeks = Math.ceil((lastDay.getDate() - firstDay.getDate() + firstDay.getDay() + 1) / 7);

        for (let i = 0; i < numberOfWeeks; i++) {
          labels.push(`Sem ${i + 1}`);
          data.push(Math.round(20000 + Math.random() * 40000));
        }
        break;

      case 'year':
        // Generate 12 months for current year
        for (let i = 0; i < 12; i++) {
          const date = new Date(currentDate.getFullYear(), i, 1);
          labels.push(date.toLocaleDateString('es-ES', { month: 'short' }));
          data.push(Math.round(50000 + Math.random() * 100000));
        }
        break;
    }

    return { labels, data };
  };

  const chartDataResponse = salesData || generateDemoData();
  const { labels, data } = chartDataResponse;

  const getPeriodLabel = () => {
    switch (selectedPeriod) {
      case 'week':
        return 'Ventas Semanales (GTQ)';
      case 'month':
        return 'Ventas Mensuales (GTQ)';
      case 'year':
        return 'Ventas Anuales (GTQ)';
      default:
        return 'Ventas (GTQ)';
    }
  };

  const chartData = {
    labels,
    datasets: [
      {
        label: getPeriodLabel(),
        data,
        borderColor: 'rgb(34, 197, 94)',
        backgroundColor: type === 'bar' 
          ? 'rgba(34, 197, 94, 0.8)' 
          : 'rgba(34, 197, 94, 0.1)',
        borderWidth: 3,
        fill: type === 'line',
        tension: 0.4,
        pointBackgroundColor: 'rgb(34, 197, 94)',
        pointBorderColor: 'white',
        pointBorderWidth: 2,
        pointRadius: 6,
        pointHoverRadius: 8,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'top' as const,
        labels: {
          font: {
            size: 14,
            family: 'Inter, system-ui, sans-serif',
          },
          color: '#374151',
          usePointStyle: true,
          pointStyle: 'circle',
        },
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: 'rgba(17, 24, 39, 0.95)',
        titleColor: '#F9FAFB',
        bodyColor: '#F9FAFB',
        borderColor: 'rgba(75, 85, 99, 0.3)',
        borderWidth: 1,
        cornerRadius: 8,
        titleFont: {
          size: 14,
          weight: 'bold' as const,
        },
        bodyFont: {
          size: 13,
        },
        callbacks: {
          label: function(context: any) {
            const value = context.parsed.y;
            const periodText = selectedPeriod === 'week' ? 'Ventas semanales' : 
                             selectedPeriod === 'month' ? 'Ventas mensuales' : 
                             'Ventas anuales';
            return `${periodText}: ${new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'GTQ',
              minimumFractionDigits: 2,
              maximumFractionDigits: 2
            }).format(value)}`;
          },
        },
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          font: {
            size: 12,
            family: 'Inter, system-ui, sans-serif',
          },
          color: '#6B7280',
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: 'rgba(107, 114, 128, 0.1)',
        },
        ticks: {
          font: {
            size: 12,
            family: 'Inter, system-ui, sans-serif',
          },
          color: '#6B7280',
          callback: function(value: any) {
            return new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'GTQ',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0,
              notation: 'compact',
              compactDisplay: 'short'
            }).format(value);
          },
        },
      },
    },
    interaction: {
      intersect: false,
      mode: 'index' as const,
    },
    elements: {
      point: {
        hoverBackgroundColor: 'rgb(34, 197, 94)',
        hoverBorderColor: 'white',
      },
    },
  };

  useEffect(() => {
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, []);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-500"></div>
        <span className="ml-2 text-gray-600">Cargando datos...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center" style={{ height }}>
        <div className="text-center">
          <p className="text-gray-500 text-sm">Error al cargar datos</p>
          <p className="text-gray-400 text-xs mt-1">Mostrando datos de ejemplo</p>
        </div>
      </div>
    );
  }

  const periodOptions = [
    { value: 'week', label: 'Semana' },
    { value: 'month', label: 'Mes' },
    { value: 'year', label: 'Año' }
  ];

  return (
    <div className="relative">
      {/* Period Selector */}
      <div className="absolute top-0 right-0 z-10">
        <div className="relative">
          <button
            onClick={() => setShowPeriodSelector(!showPeriodSelector)}
            className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
          >
            <Calendar className="h-4 w-4" />
            <span>{periodOptions.find(p => p.value === selectedPeriod)?.label}</span>
            <ChevronDown className="h-4 w-4" />
          </button>

          {showPeriodSelector && (
            <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 z-20">
              <div className="py-1">
                {periodOptions.map((option) => (
                  <button
                    key={option.value}
                    onClick={() => {
                      setSelectedPeriod(option.value as PeriodType);
                      setShowPeriodSelector(false);
                    }}
                    className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      selectedPeriod === option.value
                        ? 'bg-green-50 text-green-600 font-medium'
                        : 'text-gray-700'
                    }`}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Chart Container */}
      <div style={{ height: `${height}px` }}>
        {type === 'bar' ? (
          <Bar data={chartData} options={options} />
        ) : (
          <Line data={chartData} options={options} />
        )}
      </div>
    </div>
  );
};

export default MonthlySalesChart;